import React from 'react';
import { useResponseFormatting } from './ResponseFormattingContext';

interface TableBlockProps {
  content: string;
}

interface ParsedTable {
  headers: string[];
  rows: string[][];
}

const parseMarkdownTable = (content: string): ParsedTable | null => {
  const lines = content.trim().split('\n');
  if (lines.length < 3) return null;

  try {
    // Parse headers
    const headerLine = lines[0];
    const separatorLine = lines[1];
    
    // Check if it's a valid markdown table
    if (!separatorLine.includes('|') || !separatorLine.includes('-')) {
      return null;
    }

    const headers = headerLine
      .split('|')
      .map(cell => cell.trim())
      .filter(cell => cell.length > 0);

    // Parse rows
    const rows = lines.slice(2).map(line => 
      line
        .split('|')
        .map(cell => cell.trim())
        .filter(cell => cell.length > 0)
    ).filter(row => row.length > 0);

    return { headers, rows };
  } catch (error) {
    console.error('Table parsing error:', error);
    return null;
  }
};

export const TableBlock: React.FC<TableBlockProps> = ({ content }) => {
  const { settings } = useResponseFormatting();
  
  if (!settings.autoFormatTables) {
    return (
      <pre className="p-4 bg-muted rounded-lg border overflow-x-auto text-sm">
        {content}
      </pre>
    );
  }

  const parsedTable = parseMarkdownTable(content);
  
  if (!parsedTable) {
    return (
      <pre className="p-4 bg-muted rounded-lg border overflow-x-auto text-sm">
        {content}
      </pre>
    );
  }

  const getTableClasses = () => {
    const baseClasses = "w-full border-collapse text-sm";
    
    switch (settings.tableStyle) {
      case 'bordered':
        return `${baseClasses} border border-border`;
      case 'striped':
        return `${baseClasses}`;
      default:
        return baseClasses;
    }
  };

  const getHeaderClasses = () => {
    const baseClasses = "px-4 py-2 text-left font-medium";
    
    switch (settings.tableStyle) {
      case 'bordered':
        return `${baseClasses} border border-border bg-muted/50`;
      case 'striped':
        return `${baseClasses} bg-muted/50`;
      default:
        return `${baseClasses} border-b border-border bg-muted/50`;
    }
  };

  const getCellClasses = (rowIndex: number) => {
    const baseClasses = "px-4 py-2";
    
    switch (settings.tableStyle) {
      case 'bordered':
        return `${baseClasses} border border-border`;
      case 'striped':
        return `${baseClasses} ${rowIndex % 2 === 0 ? 'bg-muted/20' : ''}`;
      default:
        return `${baseClasses} border-b border-border`;
    }
  };

  return (
    <div className="my-4 overflow-x-auto rounded-lg border">
      <table className={getTableClasses()}>
        <thead>
          <tr>
            {parsedTable.headers.map((header, index) => (
              <th key={index} className={getHeaderClasses()}>
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {parsedTable.rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className={getCellClasses(rowIndex)}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};